import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { apiLinkAccount } from '@vrw/data/src/api/accounts.api'
import { getRedDataConfig } from '@vrw/data/src/config'
import { setToastMessage } from '@vrw/data/src/redux/toast/actions'
import { getString, LinkingJourneyOrigins, logger } from '@vrw/data/src/utils'
import { useAppDispatch, useAppSelector } from '@/redux/hooks/hooks'
import { useAccounts } from '@/query/accounts'
import { regionalizePath } from '@/dataImplementation/viewingRegionWeb'
import { getCountry } from '@vrw/data/src/redux/profile/selectors'
import { sendDatadogEvent } from '@/dataImplementation/datadog'
import { PATHS } from '@/router/paths'

const LINK_VAA_EVENTS = {
  START: 'link_vaa_start',
  START_ERROR: 'link_vaa_start_error',
  RESPONSE_SUCCESS: 'link_vaa_response_success',
  RESPONSE_ERROR: 'link_vaa_response_error',
} as const

const getJourney = (pathname: string) => {
  if (pathname.includes(PATHS.ONBOARDING_WELCOME)) return LinkingJourneyOrigins.ONBOARDING
  if (pathname.includes(PATHS.MY_ACCOUNT_LINK_ACCOUNTS)) return LinkingJourneyOrigins.ACCOUNT
  return 'unknown'
}

export const useLinkVAA = () => {
  const { pathname, hash } = useLocation()
  const isVAAState = hash.includes('state')
  const { data: accounts, refetch: refetchAccounts } = useAccounts()
  const isVAALinked = accounts?.some((account) => account.partnerCode === 'VAA')
  const { vaa: vaaConfig } = getRedDataConfig()
  const hasRun = useRef(false)
  const [linkStatus, setLinkStatus] = useState<'idle' | 'loading' | 'success' | 'failed'>('idle')
  const dispatch = useAppDispatch()
  const userRegion = useAppSelector(getCountry)

  const getPathMatch = useCallback(() => {
    const localPath = pathname.replace('/en-us', '').replace('/virgin-red', '') ?? ''
    const currentPathWithHash = `${pathname}${hash ?? ''}`
    const regionalizedPathWithHash = `${regionalizePath(localPath, userRegion)}${hash ?? ''}`

    return currentPathWithHash === regionalizedPathWithHash
  }, [pathname, hash, userRegion])

  const getEventContext = useCallback(() => {
    return {
      journey: getJourney(pathname),
      viewingRegion: userRegion,
      pathname,
      timestamp: Date.now(),
    }
  }, [pathname, userRegion])

  const linkVAAResponseHandler = useCallback(async () => {
    const startTime = Date.now()
    setLinkStatus('loading')

    try {
      if (!vaaConfig?.getVaaToken) {
        throw new Error('VAA service is not available')
      }

      const response = await vaaConfig.getVaaToken()
      logger.log('query/accounts:linkVAAResponseHandler() VAA response: ', response)

      if (response.idToken) {
        const linkResponse = await apiLinkAccount('VAA', response.idToken) // upstream BE op is synchronous - linking should be instant
        refetchAccounts() // refetch accounts to update the UI
        setLinkStatus('success')
        const duration = Date.now() - startTime
        sendDatadogEvent(LINK_VAA_EVENTS.RESPONSE_SUCCESS, {
          ...getEventContext(),
          linkResponse,
          operation_duration_ms: Math.round(duration),
        })
        return
      }

      throw new Error('query/accounts:linkVAAResponseHandler() VAA token not present.')
    } catch (error) {
      const duration = Date.now() - startTime
      logger.error(error)
      sendDatadogEvent(LINK_VAA_EVENTS.RESPONSE_ERROR, {
        ...getEventContext(),
        error,
        operation_duration_ms: Math.round(duration),
      })
      dispatch(setToastMessage(getString('api.unknownError')))
      setLinkStatus('failed')
    }
  }, [refetchAccounts, vaaConfig, dispatch, getEventContext])

  useEffect(() => {
    const handleVaaLinking = async () => {
      const pathMatch = getPathMatch()
      const canRun = !hasRun.current && pathMatch && isVAAState && !isVAALinked

      if (canRun) {
        hasRun.current = true
        await linkVAAResponseHandler()
      }
    }

    handleVaaLinking()
  }, [isVAALinked, isVAAState, linkVAAResponseHandler, getPathMatch])

  const linkVAA = useCallback(
    async (journey: LinkingJourneyOrigins) => {
      sendDatadogEvent(LINK_VAA_EVENTS.START, { ...getEventContext() })
      setLinkStatus('loading')
      try {
        await vaaConfig.handleVaaAuth(journey)
      } catch (error) {
        logger.error(error)
        sendDatadogEvent(LINK_VAA_EVENTS.START_ERROR, {
          ...getEventContext(),
          error,
        })
        dispatch(setToastMessage(getString('api.unknownError')))
        setLinkStatus('failed')
      }
    },
    [vaaConfig, dispatch, getEventContext]
  )

  return { linkVAA, linkStatus, isVAALinked }
}
